import { Cell } from "@tanstack/react-table";
import { TableCell } from "@/components/ui/table";
import { memo, useMemo } from "react";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useCheckIsProjectLocked } from "../../hooks/useCheckIsProjectLocked";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useRowSelectionCell } from "@/components/data-table/hooks/useRowSelectionCell";

interface ProjectSchedulesTableSelectRowCellProps<
  TData extends { id: string },
> {
  cell: Cell<TData, unknown>;
  id: string;
}

const ProjectSchedulesTableSelectRowCellComponent = <
  TData extends { id: string },
>({
  cell,
  id,
}: ProjectSchedulesTableSelectRowCellProps<TData>) => {
  const columnSize = cell.column.columnDef.size;
  const className = cell.column.columnDef.meta?.className;
  const { can_update } = useModulePermissions();
  const isProjectLocked = useCheckIsProjectLocked();
  const { isSelected, toggle } = useRowSelectionCell(id);

  const style = useMemo(
    () => ({ minWidth: columnSize ? `${columnSize}px` : "auto" }),
    [columnSize],
  );

  return (
    <TableCell
      key={cell.id}
      className={cn(
        className,
        "flex justify-center items-center border-t-0 p-0 h-[33px]",
      )}
      style={style}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={toggle}
        disabled={!can_update || isProjectLocked}
      />
    </TableCell>
  );
};

export const ProjectSchedulesTableSelectRowCell = memo(
  ProjectSchedulesTableSelectRowCellComponent,
) as <TData extends { id: string }>(
  props: ProjectSchedulesTableSelectRowCellProps<TData>,
) => JSX.Element;
